import { CommonModule, NgOptimizedImage } from '@angular/common';
import { CUSTOM_ELEMENTS_SCHEMA, inject, NgModule } from '@angular/core';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';

// Directives
// Material Modules
import { MatExpansionModule } from '@angular/material/expansion';
import { MatProgressBarModule } from '@angular/material/progress-bar';
import { MatTabsModule } from '@angular/material/tabs';
import { RouterModule } from '@angular/router';
import { BlockClientCardComponent } from '@core-app/blocks/block-card/block-card.component';
import { BlockChatComponent } from '@core-app/blocks/block-chat/block-chat.component';
import { BlockH1TitleComponent } from '@core-app/blocks/block-h1-title/block-h1-title.component';
import { BlockHomeHeroBannerComponent } from '@core-app/blocks/block-home-hero-banner/block-home-hero-banner.component';
import { BlockHomeSkinnyCtaBannerComponent } from '@core-app/blocks/block-home-skinny-cta-banner/block-home-skinny-cta-banner.component';
import { BlockProfileComponent } from '@core-app/blocks/block-profile/block-profile.component';
import { BlockReadabilityModalComponent } from '@core-app/blocks/block-readability/block-readability-modal.component';
import { BlockReadabilityComponent } from '@core-app/blocks/block-readability/block-readability.component';
import { BlockClientSearchResultsAllComponent } from '@core-app/blocks/block-search-results-all/block-search-results-all.component';
import { BlockSplashLandingComponent } from '@core-app/blocks/block-splash-landing-page/block-splash-landing-page.component';
import { BlockTabGroupComponent } from '@core-app/blocks/block-tab-group/block-tab-group.component';
import { BlockUserAaTableComponent } from '@core-app/blocks/block-user-aa-table/block-user-aa-table.component';
import { BlockUserDocumentsComponent } from '@core-app/blocks/block-user-documents/block-user-documents.component';
import { BlockUserScoringComponent } from '@core-app/blocks/block-user-scoring/block-user-scoring.component';
import { BlockUserSmpiTableComponent } from '@core-app/blocks/block-user-smpi-table/block-user-smpi-table.component';
import { BrandLogoComponent } from '@core-app/layout/brand-logo/brand-logo.component';
import { TokenEntryComponent } from '@core-app/layout/login/token-entry.component';
import { SearchFormComponent } from '@core-app/layout/search/search-form/search-form.component';
import { SearchResultsComponent } from '@core-app/layout/search/search-results/search-results.component';
import { SearchComponent } from '@core-app/layout/search/search.component';
import { CoreChatModule } from '@gallaghercp/core-chat';

// Custom Modules & Services
import { BlockService, DemoConfigModule, PageModule, SpinnerModule, UtilsModule } from '@gallaghercp/core-frontend';
import { SwiperModule } from '@gallaghercp/core-swiper';
import { TranslateModule } from '@ngx-translate/core';
import { AlertModule } from 'ngx-bootstrap/alert';
import { ButtonsModule } from 'ngx-bootstrap/buttons';
import { CarouselModule } from 'ngx-bootstrap/carousel';
import { CollapseModule } from 'ngx-bootstrap/collapse';

// Bootstrap Modules
import { BsDropdownModule } from 'ngx-bootstrap/dropdown';
import { ModalModule } from 'ngx-bootstrap/modal';
import { TabsModule } from 'ngx-bootstrap/tabs';
import { TooltipModule } from 'ngx-bootstrap/tooltip';
import { InfiniteScrollDirective } from 'ngx-infinite-scroll';
import { NgxMaskDirective } from 'ngx-mask';
import { ClientModule } from '../client/client.module';
import { BackToTopComponent } from './back-to-top/back-to-top.component';
import { DarkModeToggleComponent } from './dark-mode-toggle/dark-mode-toggle.component';
import { FooterComponent } from './footer/footer.component';
import { HeaderComponent } from './header/header.component';
import { LoginFaqsComponent } from './login-faqs/login-faqs.component';
import { LoginFooterComponent } from './login-footer/login-footer.component';
import { ActivateComponent } from './login/activate.component';
import { LoginComponent } from './login/login.component';

// App Components
import { MainComponent } from './main/main.component';
import { MaintenanceComponent } from './maintenance/maintenance.component';
import { SideNavFooterComponent } from './nav/side-nav/side-nav-footer/side-nav-footer.component';
import { SideNavComponent } from './nav/side-nav/side-nav.component';
import { PostThumbnailsComponent } from './nav/top-nav/post-thumbnails/post-thumbnails.component';
import { TopNavComponent } from './nav/top-nav/top-nav.component';
import { NotificationsComponent } from './notifications/notifications.component';
import { OopsComponent } from './oops/oops.component';
import { PostComponent } from './post/post.component';
import { RegisterComponent } from './register/register.component';
import { ResetConfirmComponent } from './reset/reset-confirm.component';
import { ResetRequestComponent } from './reset/reset-request.component';
import { BlockAnchorListComponent } from '../blocks/block-anchor-list/block-anchor-list.component';
import { BlockCallToActionComponent } from '../blocks/block-call-to-action/block-call-to-action.component';
import { BlockH2AnchorComponent } from '@core-app/blocks/block-h2-anchor/block-h2-anchor.component';
import { BlockGradientBackground } from '@core-app/blocks/block-gradient-bg/block-gradient-bg.component';

@NgModule({
    declarations: [
        MainComponent,
        MaintenanceComponent,
        OopsComponent,
        HeaderComponent,
        FooterComponent,
        SideNavComponent,
        SideNavFooterComponent,
        TopNavComponent,
        ActivateComponent,
        LoginComponent,
        RegisterComponent,
        BlockProfileComponent,
        LoginFooterComponent,
        ResetRequestComponent,
        ResetConfirmComponent,
        LoginFaqsComponent,
        TokenEntryComponent,
        BackToTopComponent,
        NotificationsComponent,
        PostComponent,
        PostThumbnailsComponent,
        DarkModeToggleComponent,
        BlockChatComponent,
        BlockH1TitleComponent,
        BlockReadabilityComponent,
        BlockReadabilityModalComponent,
        BlockSplashLandingComponent,
        BlockHomeHeroBannerComponent,
        BlockClientCardComponent,
        BlockClientSearchResultsAllComponent,
        SearchResultsComponent,
        SearchComponent,
        SearchFormComponent,
        BlockUserScoringComponent,
        BrandLogoComponent,
        BlockHomeSkinnyCtaBannerComponent,
        BlockUserDocumentsComponent,
        BlockUserSmpiTableComponent,
        BlockUserAaTableComponent,
        BlockAnchorListComponent,
        BlockCallToActionComponent,
        BlockH2AnchorComponent,
        BlockGradientBackground,
    ],
    imports: [
        CommonModule,
        RouterModule,
        BsDropdownModule,
        CollapseModule,
        ModalModule,
        ButtonsModule,
        CarouselModule,
        TabsModule,
        FormsModule,
        ReactiveFormsModule,
        MatProgressBarModule,
        SpinnerModule,
        AlertModule,
        TranslateModule,
        MatTabsModule,
        PageModule,
        MatExpansionModule,
        UtilsModule,
        TooltipModule,
        ClientModule,
        SwiperModule,
        CoreChatModule,
        DemoConfigModule,
        InfiniteScrollDirective,
        NgOptimizedImage,
        NgxMaskDirective,
        BlockTabGroupComponent,
    ],
    exports: [
        MainComponent,
        CommonModule,
        HeaderComponent,
        NotificationsComponent,
        FooterComponent,
        BackToTopComponent,
        DarkModeToggleComponent,
        LoginFooterComponent,
        SideNavComponent,
        PostThumbnailsComponent,
        SearchComponent,
        SearchFormComponent,
    ],
    schemas: [CUSTOM_ELEMENTS_SCHEMA],
})
export class LayoutModule {
    protected blocks = inject(BlockService);

    constructor() {
        // this.blocks.addBlock('H1-TITLE', BlockH1TitleComponent);
        this.blocks.addBlock('SPLASH-LANDING', BlockSplashLandingComponent);
        this.blocks.addBlock('HOME-HERO-BANNER', BlockHomeHeroBannerComponent);
        this.blocks.addBlock('CARD', BlockClientCardComponent);
        this.blocks.addBlock('SEARCH-RESULTS-ALL', BlockClientSearchResultsAllComponent);
        this.blocks.addBlock('USER-SCORING', BlockUserScoringComponent);
        this.blocks.addBlock('HOME-SKINNY-CTA-BANNER', BlockHomeSkinnyCtaBannerComponent);
        this.blocks.addBlock('USER-DOCUMENTS', BlockUserDocumentsComponent);
        this.blocks.addBlock('USER-DATA-TABLE', BlockUserSmpiTableComponent);
        this.blocks.addBlock('USER-AA-TABLE', BlockUserAaTableComponent);
        this.blocks.addBlock('TAB-GROUP', BlockTabGroupComponent);
        this.blocks.addBlock('ANCHOR-LIST', BlockAnchorListComponent);
        this.blocks.addBlock('CALL-TO-ACTION', BlockCallToActionComponent);
        this.blocks.addBlock('H2-ANCHOR', BlockH2AnchorComponent);
        this.blocks.addBlock('GRADIENT-BG', BlockGradientBackground);
    }
}
